import { useCallback, useEffect, useMemo, useState } from "react";
import { ExtractedRecord, TargetSchema, type DocumentTransformation, type TransformationTemplate } from "@/types";
import { useSchemaDefinitions } from "@/hooks/useSchemaDefinitions";
import type { SchemaFieldDefinition } from "@/constants/schema-fields/schemaFields";
import {
  type MappingValidationError,
  validateAllMappings,
  validateAllTemplates,
} from "../utils/fieldMappingValidation";
import { buildReverseHeaderMap, buildHeaderMappings } from "../utils/fieldMappingUtils";
import { getRecordSchemas } from "../utils/recordUtils";

interface UseFieldMappingStateProps {
  /** The document transformation carrying the schema analyses */
  transformation: DocumentTransformation;
  /**
   * Extracted records to build the mapping preview from (#6755).
   *
   * Passed in rather than read off the transformation, which no longer carries
   * them — `?expand=extracted_records` was removed because serialising a
   * 497,711-record set OOM-kills mgmt-api (#6826). The mapping surfaces only
   * ever needed a sample, so one page is enough.
   */
  records?: ExtractedRecord[];
  /** Array of available transformation templates */
  transformationTemplates: TransformationTemplate[];
  /** Index of the currently selected schema tab */
  selectedSchemaIndex: number;
  /** Whether to show unmapped headers in the mapping table */
  showUnmapped: boolean;
}

/**
 * Hook to manage field mapping state and validation in the FieldMappingDialog
 *
 * Handles:
 * - Managing edited field mappings per schema
 * - Validating mappings for duplicates and unknown fields
 * - Building header mappings from transformation data and template
 * - Tracking which schemas have been edited
 * - Computing validation state (valid vs invalid schemas)
 * - Determining save eligibility
 * - Initializing mappings for newly selected schemas
 *
 * State Management:
 * - `editedMappingsBySchema`: Tracks user edits per schema (source header → target field)
 * - `validationErrors`: Real-time validation errors per schema
 * - `editedSchemas`: Derived set of schemas that have been modified
 *
 * Validation Flow:
 * 1. User changes a mapping (via handleMappingChange)
 * 2. editedMappingsBySchema updates
 * 3. useEffect triggers validation for all edited schemas
 * 4. validationErrors updates
 * 5. validSchemas/invalidSchemas recompute
 * 6. canSave updates based on valid schemas count
 *
 * Schema Selection:
 * - When user switches tabs (selectedSchemaIndex changes)
 * - Hook loads that schema's mappings
 * - If not yet initialized, creates initial mappings from template
 * - Returns current schema's data (headerMappings, availableFields, etc.)
 *
 * @param props - Configuration for field mapping state management
 * @returns Mapping data, validation state, and handlers for the current schema
 *
 * @example
 * ```tsx
 * const {
 *   template,
 *   schemaAnalyses,
 *   headerMappings,
 *   currentEditedMappings,
 *   validationErrors,
 *   validSchemas,
 *   invalidSchemas,
 *   canSave,
 *   handleMappingChange,
 * } = useFieldMappingState({
 *   transformation,
 *   records,
 *   transformationTemplates,
 *   selectedSchemaIndex: 0,
 *   showUnmapped: true,
 * });
 *
 * // Render mapping table
 * <MappingTable
 *   headerMappings={headerMappings}
 *   editedMappings={currentEditedMappings}
 *   onMappingChange={handleMappingChange}
 * />
 *
 * // Show validation status
 * {validSchemas.length} valid, {invalidSchemas.length} invalid
 *
 * // Enable/disable save
 * <Button disabled={!canSave}>Save</Button>
 * ```
 */
export const useFieldMappingState = ({
  transformation,
  records,
  transformationTemplates,
  selectedSchemaIndex,
  showUnmapped,
}: UseFieldMappingStateProps) => {
  // Track edited mappings per schema (schema key → source header → target field)
  const [editedMappingsBySchema, setEditedMappingsBySchema] = useState<Record<string, Record<string, string[]>>>({});

  // Track which schemas have actual user changes (not just initialization)
  const [schemasWithChanges, setSchemasWithChanges] = useState<Set<string>>(new Set());

  // Track validation errors - SINGLE SOURCE OF TRUTH
  const [validationErrors, setValidationErrors] = useState<Record<string, MappingValidationError[]>>({});

  // Load schemas from API to validate selections and provide mapping options
  const { data: schemaDefinitions, loading: schemasLoading } = useSchemaDefinitions();

  // Get all schema analyses from the transformation
  const schemaAnalyses = useMemo(() => {
    const existingAnalyses = transformation.coverage_analysis?.schema_analyses || [];
    const targetSchemas = transformation.target_schemas || [];

    // Filter existing analyses to only allow those in target_schemas
    // This ensures tabs are strictly limited to the target schemas
    const targetSchemaSet = new Set(targetSchemas);
    const rawRelevantAnalyses = existingAnalyses.filter((a) => targetSchemaSet.has(a.target_schema));

    // Deduplicate analyses by target_schema (API may return duplicates)
    const uniqueAnalysesMap = new Map();
    rawRelevantAnalyses.forEach((analysis) => {
      if (!uniqueAnalysesMap.has(analysis.target_schema)) {
        uniqueAnalysesMap.set(analysis.target_schema, analysis);
      }
    });

    const relevantAnalyses = Array.from(uniqueAnalysesMap.values());

    const existingSchemaSet = new Set(relevantAnalyses.map((a) => a.target_schema));
    const missingSchemas = targetSchemas.filter((s) => !existingSchemaSet.has(s));

    if (missingSchemas.length === 0) return relevantAnalyses;

    // Create placeholder analyses for missing schemas so they appear in tabs
    const newAnalyses = missingSchemas.map((schema) => ({
      target_schema: schema,
      expected_fields: schemaDefinitions?.schemas[schema]?.fields.map((f) => f.field) || [],
      unpopulated_fields: {},
    }));

    return [...relevantAnalyses, ...newAnalyses];
  }, [transformation, schemaDefinitions]);

  // Get the template used for this transformation (or first available)
  const template = useMemo(() => {
    if (!transformation.transformation_template_id_ref) {
      return transformationTemplates?.[0];
    }
    return transformationTemplates?.find((t) => t.id === transformation.transformation_template_id_ref);
  }, [transformationTemplates, transformation]);

  // Safely get selected schema (handle invalid indices)
  const safeSchemaIndex = Math.min(selectedSchemaIndex, schemaAnalyses.length - 1);
  const selectedSchema = schemaAnalyses[safeSchemaIndex] || null;

  // Derive edited schemas from the mappings object (any schema with edits)
  const editedSchemas = useMemo(() => new Set(Object.keys(editedMappingsBySchema)), [editedMappingsBySchema]);

  // Build available fields map for validation (schema → set of valid field names)
  const availableFieldsBySchema = useMemo(() => {
    const fieldsBySchema: Record<string, Set<string>> = {};

    schemaAnalyses.forEach((schema) => {
      const fields = schemaDefinitions?.schemas[schema.target_schema]?.fields || [];
      fieldsBySchema[schema.target_schema] = new Set(fields.map((f) => f.field));
    });

    return fieldsBySchema;
  }, [schemaAnalyses, schemaDefinitions]);

  // Validate whenever mappings change
  useEffect(() => {
    const errors = validateAllMappings(editedMappingsBySchema, editedSchemas, availableFieldsBySchema);
    setValidationErrors(errors);
  }, [editedMappingsBySchema, editedSchemas, availableFieldsBySchema]);

  // Reverse map of source header → target fields from the template
  const reverseHeaderMap = useMemo(() => buildReverseHeaderMap(template, selectedSchema), [template, selectedSchema]);

  // Get detected headers from transformation metadata
  const detectedHeaders = useMemo(() => {
    return transformation.processing_metadata?.detected_headers || [];
  }, [transformation]);

  // Get the extracted records for this schema
  const extractedRecords = useMemo(() => {
    return (records ?? []).filter((r) => getRecordSchemas(r).includes(selectedSchema?.target_schema as TargetSchema));
  }, [records, selectedSchema]);

  // Build the complete header mappings list (mapped + optionally unmapped)
  const headerMappings = useMemo(() => {
    return buildHeaderMappings(extractedRecords, reverseHeaderMap, detectedHeaders, showUnmapped);
  }, [extractedRecords, reverseHeaderMap, detectedHeaders, showUnmapped]);

  // Get available fields for the selected schema (for dropdown options)
  const availableFields = useMemo(() => {
    if (!selectedSchema?.target_schema) return [];
    return schemaDefinitions?.schemas[selectedSchema.target_schema]?.fields || [];
  }, [selectedSchema, schemaDefinitions]);

  const currentSchemaKey = selectedSchema?.target_schema || "";

  // Initialize ALL schemas on mount (not just selected one)
  useEffect(() => {
    if (!template || schemaAnalyses.length === 0) return;

    setEditedMappingsBySchema((prev) => {
      // Skip if we've already initialized all schemas
      if (Object.keys(prev).length === schemaAnalyses.length) return prev;

      const allMappings = { ...prev };

      schemaAnalyses.forEach((schema) => {
        const schemaKey = schema.target_schema;
        if (allMappings[schemaKey]) return;

        const schemaReverseMap = buildReverseHeaderMap(template, schema);
        const schemaRecords = (records ?? []).filter((r) => getRecordSchemas(r).includes(schemaKey as TargetSchema));
        const mappings = buildHeaderMappings(schemaRecords, schemaReverseMap, detectedHeaders, false);

        allMappings[schemaKey] = mappings.reduce(
          (acc, m) => {
            acc[m.detectedHeader] = m.targetFields;
            return acc;
          },
          {} as Record<string, string[]>
        );
      });

      return allMappings;
    });
  }, [template, schemaAnalyses, transformation, detectedHeaders]);

  // Get current mappings for this schema (derived, no side effects)
  const currentEditedMappings = editedMappingsBySchema[currentSchemaKey] || {};

  /**
   * Handle mapping change for a header
   * Updates the mapping for the current schema and marks it as edited
   *
   * @param header - The detected header name
   * @param targetField - The target schema field to map to
   */
  const handleMappingChange = useCallback(
    (header: string, targetFields: string[]) => {
      const schemaKey = selectedSchema?.target_schema || "";

      setEditedMappingsBySchema((prev) => ({
        ...prev,
        [schemaKey]: {
          ...(prev[schemaKey] || {}),
          [header]: targetFields,
        },
      }));

      // Mark this schema as having user changes
      setSchemasWithChanges((prev) => new Set(prev).add(schemaKey));
    },
    [selectedSchema]
  );

  // ── Unified cross-schema mapping (#6101) ──────────────────────────────────
  // The horizontal source-mirrored remap view maps each source column to a
  // field in ANY schema from a single dropdown, so it needs the full set of
  // schema keys, every schema's field definitions, and a change handler that
  // writes across schema buckets for one header.

  const allSchemaKeys = useMemo(() => schemaAnalyses.map((s) => s.target_schema as string), [schemaAnalyses]);

  const fieldsBySchema = useMemo(() => {
    const map: Record<string, SchemaFieldDefinition[]> = {};
    schemaAnalyses.forEach((s) => {
      map[s.target_schema] = schemaDefinitions?.schemas[s.target_schema]?.fields || [];
    });
    return map;
  }, [schemaAnalyses, schemaDefinitions]);

  /**
   * Set the complete cross-schema mapping for one source header. `targets`
   * lists every (schema, field) the header maps to across ALL schemas; this
   * writes each schema's bucket and clears the header from schemas no longer
   * targeted, so the single unified dropdown stays the source of truth.
   */
  const handleUnifiedMappingChange = useCallback(
    (header: string, targets: { schema: string; field: string }[]) => {
      const bySchema = new Map<string, string[]>();
      for (const t of targets) {
        const arr = bySchema.get(t.schema) ?? [];
        arr.push(t.field);
        bySchema.set(t.schema, arr);
      }
      const touched: string[] = [];
      setEditedMappingsBySchema((prev) => {
        const next = { ...prev };
        for (const schemaKey of allSchemaKeys) {
          const hadHeader = (prev[schemaKey]?.[header]?.length ?? 0) > 0;
          if (bySchema.has(schemaKey)) {
            next[schemaKey] = { ...(prev[schemaKey] || {}), [header]: bySchema.get(schemaKey)! };
            touched.push(schemaKey);
          } else if (hadHeader) {
            next[schemaKey] = { ...(prev[schemaKey] || {}), [header]: [] };
            touched.push(schemaKey);
          }
        }
        return next;
      });
      if (touched.length) {
        setSchemasWithChanges((prev) => {
          const n = new Set(prev);
          touched.forEach((s) => n.add(s));
          return n;
        });
      }
    },
    [allSchemaKeys]
  );

  const unmappedCount = headerMappings.filter((m) => !m.isMapped).length;

  // Derive valid schemas (no validation errors) - only from schemas with actual changes
  const validSchemas = useMemo(() => {
    return Array.from(schemasWithChanges).filter(
      (schema) => !validationErrors[schema] || validationErrors[schema].length === 0
    );
  }, [schemasWithChanges, validationErrors]);

  // Derive invalid schemas (have validation errors) - only from schemas with actual changes
  const invalidSchemas = useMemo(() => {
    return Array.from(schemasWithChanges).filter(
      (schema) => validationErrors[schema] && validationErrors[schema].length > 0
    );
  }, [schemasWithChanges, validationErrors]);

  // Can save if there are actual user changes and at least one valid schema
  const canSave = schemasWithChanges.size > 0 && validSchemas.length > 0;

  return {
    /** The transformation template being used */
    template,
    /** All available schema analyses from the transformation */
    schemaAnalyses,
    /** Currently selected schema analysis */
    selectedSchema,
    /** Header mappings for the current schema (mapped + optionally unmapped) */
    headerMappings,
    /** Available field options for the current schema */
    availableFields,
    /** Current edited mappings for the selected schema (source header → target field) */
    currentEditedMappings,
    /** All edited mappings organized by schema key */
    editedMappingsBySchema,
    /** Set of schema keys with actual user changes (not just initialization) */
    schemasWithChanges,
    /** Validation errors per schema (only includes schemas with errors) */
    validationErrors,
    /** Count of unmapped headers in the current schema */
    unmappedCount,
    /** Array of schema keys that passed validation */
    validSchemas,
    /** Array of schema keys that failed validation */
    invalidSchemas,
    /** Whether save should be enabled (has actual user changes and at least one valid schema) */
    canSave,
    /** Handler to update a header's target field mapping */
    handleMappingChange,
    /** All target schema keys present on this transformation (#6101). */
    allSchemaKeys,
    /** Full field definitions per schema — union source for the unified dropdown (#6101). */
    fieldsBySchema,
    /** Set a header's complete cross-schema mapping in one call (#6101). */
    handleUnifiedMappingChange,
    /** Whether schema definitions are still loading */
    isLoading: schemasLoading,
  };
};

/**
 * Simplified hook for validating current mappings in DocumentReviewTab
 *
 * This checks the existing template mappings without managing edit state.
 * Used to determine if a document has mapping issues that require review/fixing.
 *
 * Validates:
 * - Unknown fields (fields that don't exist in the schema)
 * - Duplicate mappings (multiple headers mapped to the same field)
 *
 * @param transformation - The document transformation (null if not loaded)
 * @param template - The transformation template (undefined if not loaded)
 * @param availableSchemas - Array of schemas to validate
 * @param records - Loaded extracted records (#6755 — the transformation row no
 *   longer carries them; validating off `transformation.extracted_records`
 *   would silently pass every schema and disarm the approval gate)
 * @param recordsComplete - True when every page is loaded; when false, a
 *   schema with no loaded record reports "not validated" instead of passing
 * @returns Validation issues array and boolean flag
 *
 * @example
 * ```tsx
 * const { hasValidationIssues, validationIssues } =
 *   useFieldMappingReviewValidation(
 *     latestTransformation,
 *     template,
 *     availableSchemas,
 *     loadedRecords,
 *     !hasMoreRecords
 *   );
 *
 * // Show alert if issues exist
 * {hasValidationIssues && (
 *   <Alert severity=AlertType.ERROR>
 *     {validationIssues.map(issue => (
 *       <div key={issue.schema}>
 *         {issue.schema}: {issue.message}
 *       </div>
 *     ))}
 *   </Alert>
 * )}
 * ```
 */
export const useFieldMappingReviewValidation = (
  transformation: DocumentTransformation | null,
  template: TransformationTemplate | undefined,
  availableSchemas: TargetSchema[],
  records: ExtractedRecord[],
  recordsComplete: boolean
) => {
  const { data: schemaDefinitions } = useSchemaDefinitions();

  const validationIssues = useMemo(() => {
    return validateAllTemplates(
      transformation,
      template,
      availableSchemas,
      schemaDefinitions,
      records,
      recordsComplete
    );
  }, [transformation, template, availableSchemas, schemaDefinitions, records, recordsComplete]);

  return {
    /** Whether any validation issues exist across all schemas */
    hasValidationIssues: validationIssues.length > 0,
    /** Array of validation issues (schema + message) */
    validationIssues,
  };
};
